/**
 * Custom React hook to check if the current user has permission to perform specified actions.
 *
 * This hook retrieves the user's roles from the authentication context, aggregates their permissions
 * based on a role schema, and returns an array of booleans indicating whether the user can perform
 * each action in the provided list.
 *
 * @param actions - An array of action strings to check permissions for.
 * @returns An array of booleans, where each boolean corresponds to whether the user can perform the action at the same index in the input array.
 *
 * @example
 * // Check if the user can 'user_roles_update' and 'user_roles_delete'
 * const [canEditUser, canViewUser] = usePermissions(['user_roles_update', 'user_roles_delete']);
 * if (canEditEditUser) {
 *   // Show edit button
 * }
 * if (canDeleteUser) {
 *   // Show delete button
 * }
 *
 * @example
 * // Check a single permission
 * const [canViewUserRoles] = usePermissions(['user_roles_view']);
 * if (canViewUserRoles) {
 *   // Render user roles view
 * }
 */

import { useContext } from 'react';
import { AuthContext } from 'modules/shared/context/AuthContext';
import * as permissions from 'modules/shared/constants/permissions';
import roleSchema from 'modules/shared/utils/role_schema.json';

type RoleSchemaType = {
  roles: {
    [role: string]: {
      enforce_permissions: boolean;
      permissions: string[];
      auth_name: string;
    };
  };
  permissions: string[];
};

const roles = Object.values(roleSchema.roles);

type Permission = (typeof permissions)[keyof typeof permissions];

export const getUsersPermissions = (userScopes: string[]) => {
  return userScopes.reduce<string[]>((acc, scope) => {
    const role = roleSchema.roles[scope];

    if (role?.enforce_permissions) {
      return [...acc, ...(role?.permissions || [])];
    }

    return acc;
  }, []);
};

export const getShouldEnforcePermissions = (usersScopes: string[]) => {
  return roles.some((role) => usersScopes?.includes(role?.auth_name) && role?.enforce_permissions);
};

export const usePermissions = (permissions: Permission[]) => {
  const { user } = useContext(AuthContext);

  /**
   * The enforce check is temporary and will be removed once all roles are fully enforced via RBAC.
   * It preserves existing behavior for roles that are not yet governed by the role-based access control system.
   */
  const enforcePermissions = getShouldEnforcePermissions(user?.scopes || []);
  const usersPermissions = getUsersPermissions(user?.scopes || []);

  const canUserPerformActions = (perms: readonly Permission[]) =>
    perms.map((p) => {
      if (!enforcePermissions) return true; // TODO: Remove this line once all roles are fully enforced via RBAC.

      return usersPermissions?.includes(p) || false;
    });

  return canUserPerformActions(permissions);
};
